<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/ops/dashboard">Ops</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Lookup lists</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Lookup lists</h1>
        <p class="page-head__subtitle">
          Whether a receipt the assessment calls loan money really is loan money is decided by
          these lists, not by the AI. Every assessment records the version of each list it used;
          a change applies to later assessments only.
        </p>
      </div>
    </div>
  </header>

  @if (isLoading()) {
    <div class="dash-panel">
      <ul class="ll-skeleton">
        @for (r of [1, 2, 3, 4, 5]; track r) {
          <li><span class="sg-skeleton sg-skeleton--text" style="width: 55%"></span></li>
        }
      </ul>
    </div>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load the lookup lists</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button class="ll-btn" type="button" (click)="reload()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else {
    <div class="ll-tabs" role="tablist" aria-label="Lookup lists">
      @for (t of tabs; track t.kind) {
        <button
          class="ll-tab"
          type="button"
          role="tab"
          [attr.aria-selected]="selected() === t.kind"
          [class.ll-tab--active]="selected() === t.kind"
          (click)="select(t.kind)"
        >
          {{ t.label }} <span class="ll-tab__count">{{ countFor(t.kind) }}</span>
        </button>
      }
    </div>

    <section class="dash-panel" role="tabpanel">
      <div class="dash-panel__header">
        <div>
          <h2 class="dash-panel__title">{{ tab().label }}</h2>
          <p class="dash-panel__subtitle">
            {{ tab().effect }}
            @if (version()) {
              · List version {{ version() | date: 'yyyy-MM-dd HH:mm' }}
            }
          </p>
        </div>
      </div>

      @if (canWrite()) {
        <form class="ll-add" [formGroup]="addForm" (ngSubmit)="submitAdd()">
          <mat-form-field appearance="outline" class="ll-add__term">
            <mat-label>Term</mat-label>
            <input matInput formControlName="term" data-testid="add-term" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="ll-add__reason">
            <mat-label>Reason (required)</mat-label>
            <input matInput formControlName="reason" data-testid="add-reason" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="ll-add__app">
            <mat-label>Prompting application (required)</mat-label>
            <input matInput formControlName="prompted_by" data-testid="add-app" />
            <mat-hint>The application's 8-character id</mat-hint>
            @if (addForm.controls.prompted_by.hasError('pattern')) {
              <mat-error>An application id is 8 letters or digits</mat-error>
            }
          </mat-form-field>
          <button
            mat-flat-button
            color="primary"
            type="submit"
            class="ll-add__submit"
            [disabled]="isSaving()"
            data-testid="add-submit"
          >
            <app-icon name="plus" [size]="16" /> Add
          </button>
        </form>
      }
      @if (saveError()) {
        <p class="ll-error" role="alert">{{ saveError() }}</p>
      }

      <div class="ll-table-wrap">
        <table class="ll-table">
          <thead>
            <tr>
              <th>Term</th>
              <th>Added</th>
              <th>Reason</th>
              <th>Application</th>
              @if (canWrite()) {
                <th class="ll-table__action"><span class="sr-only">Actions</span></th>
              }
            </tr>
          </thead>
          <tbody>
            @for (e of active(); track e.short_id) {
              <tr class="ll-row" data-testid="active-row">
                <td class="ll-row__term">{{ e.term }}</td>
                <td class="ll-row__meta">
                  {{ e.added_by_name ?? 'Rulebook seed' }}
                  <span class="ll-row__when">{{ e.added_at | date: 'yyyy-MM-dd' }}</span>
                </td>
                <td class="ll-row__reason">{{ e.reason }}</td>
                <td class="ll-row__meta">
                  @if (e.prompted_by_application) {
                    <a [routerLink]="['/ops/kickstart', e.prompted_by_application]">{{
                      e.prompted_by_application
                    }}</a>
                  } @else {
                    —
                  }
                </td>
                @if (canWrite()) {
                  <td class="ll-table__action">
                    @if (removing() !== e.short_id) {
                      <button class="ll-btn ll-btn--quiet" type="button" (click)="openRemove(e)">
                        Remove
                      </button>
                    }
                  </td>
                }
              </tr>
              @if (removing() === e.short_id) {
                <tr class="ll-row ll-row--remove">
                  <td [attr.colspan]="canWrite() ? 5 : 4">
                    <form
                      class="ll-remove"
                      [formGroup]="removeForm"
                      (ngSubmit)="submitRemove(e)"
                      data-testid="remove-form"
                    >
                      <mat-form-field appearance="outline" class="ll-remove__reason">
                        <mat-label>Why is “{{ e.term }}” coming off the list? (required)</mat-label>
                        <input matInput formControlName="reason" data-testid="remove-reason" />
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="ll-remove__app">
                        <mat-label>Prompting application (required)</mat-label>
                        <input matInput formControlName="prompted_by" data-testid="remove-app" />
                        <mat-hint>The application's 8-character id</mat-hint>
                        @if (removeForm.controls.prompted_by.hasError('pattern')) {
                          <mat-error>An application id is 8 letters or digits</mat-error>
                        }
                      </mat-form-field>
                      <button
                        mat-flat-button
                        color="warn"
                        type="submit"
                        [disabled]="isSaving()"
                        data-testid="remove-submit"
                      >
                        Remove from list
                      </button>
                      <button mat-stroked-button type="button" (click)="cancelRemove()">
                        Cancel
                      </button>
                    </form>
                  </td>
                </tr>
              }
            } @empty {
              <tr>
                <td [attr.colspan]="canWrite() ? 5 : 4" class="ll-empty">
                  Nothing on this list. Every receipt the assessment calls loan money stays in
                  revenue until a term is added here.
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (removed().length) {
        <h3 class="ll-removed-title">Removed — applied to later assessments only</h3>
        <div class="ll-table-wrap">
          <table class="ll-table ll-table--removed">
            <thead>
              <tr>
                <th>Term</th>
                <th>Added</th>
                <th>Removed</th>
                <th>Removal reason</th>
                <th>Application</th>
              </tr>
            </thead>
            <tbody>
              @for (e of removed(); track e.short_id) {
                <tr class="ll-row ll-row--removed" data-testid="removed-row">
                  <td class="ll-row__term"><s>{{ e.term }}</s></td>
                  <td class="ll-row__meta">
                    {{ e.added_by_name ?? 'Rulebook seed' }}
                    <span class="ll-row__when">{{ e.added_at | date: 'yyyy-MM-dd' }}</span>
                  </td>
                  <td class="ll-row__meta">
                    {{ e.removed_by_name }}
                    <span class="ll-row__when">{{ e.removed_at | date: 'yyyy-MM-dd' }}</span>
                  </td>
                  <td class="ll-row__reason">{{ e.removal_reason }}</td>
                  <td class="ll-row__meta">
                    <span class="ll-row__app">
                      Added:
                      @if (e.prompted_by_application) {
                        <a [routerLink]="['/ops/kickstart', e.prompted_by_application]">{{
                          e.prompted_by_application
                        }}</a>
                      } @else {
                        —
                      }
                    </span>
                    <span class="ll-row__app" data-testid="removal-app">
                      Removed:
                      @if (e.removal_prompted_by_application) {
                        <a [routerLink]="['/ops/kickstart', e.removal_prompted_by_application]">{{
                          e.removal_prompted_by_application
                        }}</a>
                      } @else {
                        —
                      }
                    </span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>
  }
</div>
